// Typing stays put. On a sheet or widget with a text box, tapping one of its buttons
// (a chip, +/−, a slider, Save) would normally take focus from the text box: the
// keyboard (the phone's, or ours on the wall: ui/TouchKeyboard) drops, the page
// shrinks back, and everything jumps. Here such a tap keeps focus where it was; the
// button still works, and the keyboard stays up until it's hidden or the sheet closes.

type Field = HTMLInputElement | HTMLTextAreaElement;

const TEXT_TYPES = new Set(["text", "search", "email", "url", "tel", "password", "number"]);

export function isTextField(el: unknown): el is Field {
  if (el instanceof HTMLTextAreaElement) return !el.readOnly && !el.disabled;
  if (el instanceof HTMLInputElement) return TEXT_TYPES.has(el.type) && !el.readOnly && !el.disabled;
  return false;
}

/** A text box left with focus still counts as typing for this long after the last touch. */
export const TYPING_HOLD_MS = 15 * 60_000;

/** Someone is typing: a text box has focus. Screens don't switch or move under them. */
export function isTyping(): boolean {
  return isTextField(document.activeElement);
}

// Controls that take a tap without needing focus themselves.
const TAPPABLE = 'button, [role="button"], [role="slider"], [role="tab"], [role="switch"], [role="option"], .chip, label';
// What a text box and the thing tapped must share for focus to stay.
const SCOPE = ".sheet, .widget, .osk, [data-keep-typing]";

export function shouldKeepFocus(field: Element | null, target: Element | null): boolean {
  if (!isTextField(field) || !target || target === field) return false;
  // Another text box, a select, a date or a checkbox takes focus as usual.
  if (target.closest("input, textarea, select, [contenteditable]")) return false;
  const control = target.closest(TAPPABLE);
  if (!control || control.querySelector("input, textarea, select")) return false; // a label for another field focuses it
  if (control.closest("a[href]")) return false;
  const scope = field.closest(SCOPE);
  return Boolean(scope && scope.contains(control));
}

export function installKeepTyping() {
  // mousedown is where browsers move focus (touch taps get one too, after the touch).
  document.addEventListener(
    "mousedown",
    (e) => {
      if (shouldKeepFocus(document.activeElement, e.target instanceof Element ? e.target : null)) e.preventDefault();
    },
    true,
  );
}
